import { createRouteRef, createPlugin, createComponentExtension, useApi, configApiRef, githubAuthApiRef } from '@backstage/core-plugin-api';
import React, { useState, useCallback } from 'react';
import { Box, makeStyles, Paper, ButtonGroup, Button as Button$1, Grid, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { InfoCard, Progress, StructuredMetadataTable, Table, Link, MissingAnnotationEmptyState, Page, Content, ContentHeader, SupportButton } from '@backstage/core-components';
import { useAsync, useAsyncFn } from 'react-use';
import { Octokit } from '@octokit/rest';
import { getEntitySourceLocation } from '@backstage/catalog-model';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import NoteRoundedIcon from '@material-ui/icons/NoteRounded';
import { useEntity } from '@backstage/plugin-catalog-react';
import GitHubIcon from '@material-ui/icons/GitHub';
import moment from 'moment';
import { makeStyles as makeStyles$1, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Chip from '@material-ui/core/Chip';
import Box$1 from '@material-ui/core/Box';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { graphql } from '@octokit/graphql';
import { DateTime } from 'luxon';
import { Routes, Route } from 'react-router';

const entityContentRouteRef = createRouteRef({
  id: "security-insights"
});
const securityInsightsPlugin = createPlugin({
  id: "security-insights",
  routes: {
    entityContent: entityContentRouteRef
  }
});
const EntitySecurityInsightsContent = securityInsightsPlugin.provide(
  createComponentExtension({
    name: "EntitySecurityInsightsContent",
    component: {
      lazy: () => import('./index-6e068a2c.esm.js').then(
        (m) => m.SecurityInsightsTab
      )
    }
  })
);
const EntityGithubDependabotContent = securityInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubDependabotContent",
    component: {
      lazy: () => import('./index-a0342291.esm.js').then(
        (m) => m.GithubDependabotTab
      )
    }
  })
);
const EntitySecurityInsightsCard = securityInsightsPlugin.provide(
  createComponentExtension({
    name: "EntitySecurityInsightsCard",
    component: {
      lazy: () => import('./index-30634ed4.esm.js').then(
        (m) => m.SecurityInsightsWidget
      )
    }
  })
);
const EntityDependabotAlertsCard = securityInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityDependabotAlertsCard",
    component: {
      lazy: () => import('./index-c2fda4d3.esm.js').then(
        (m) => m.DependabotAlertsWidget
      )
    }
  })
);

const useProjectEntity = (entity) => {
  var _a, _b;
  const projectSlug = (_b = (_a = entity.metadata) == null ? void 0 : _a.annotations) == null ? void 0 : _b["github.com/project-slug"];
  return {
    owner: projectSlug.split("/")[0],
    repo: projectSlug.split("/")[1]
  };
};

const useGitHubConfig = (entity) => {
  var _a;
  const config = useApi(configApiRef);
  const providerConfigs = (_a = config.getOptionalConfigArray("integrations.github")) != null ? _a : [];
  const location = getEntitySourceLocation(entity);
  if (location.type === "url" && location.target.length > 0) {
    const locationHost = new URL(location.target).host;
    return providerConfigs.find(
      (providerConfig) => (providerConfig == null ? void 0 : providerConfig.getOptionalString("host")) === locationHost
    );
  }
  return providerConfigs[0];
};

const useUrl = (entity) => {
  const targetProviderConfig = useGitHubConfig(entity);
  const baseUrl = (targetProviderConfig == null ? void 0 : targetProviderConfig.getOptionalString("apiBaseUrl")) || "https://api.github.com";
  const hostname = (targetProviderConfig == null ? void 0 : targetProviderConfig.getOptionalString("host")) || "github.com";
  return {
    hostname,
    baseUrl
  };
};

const getSeverityBadge = (severityLevel, numberOfIssues) => {
  switch (severityLevel) {
    case "warning":
      return /* @__PURE__ */ React.createElement(
        Box,
        {
          display: "flex",
          alignItems: "center",
          fontWeight: "fontWeightLight",
          mr: 2,
          style: { color: "#f57c00" }
        },
        /* @__PURE__ */ React.createElement(WarningRoundedIcon, { fontSize: "small" }),
        "\xA0 ",
        numberOfIssues,
        " Warning"
      );
    case "error":
      return /* @__PURE__ */ React.createElement(
        Box,
        {
          display: "flex",
          alignItems: "center",
          fontWeight: "fontWeightLight",
          mr: 2,
          style: { color: "#d32f2f" }
        },
        /* @__PURE__ */ React.createElement(ErrorRoundedIcon, { fontSize: "small" }),
        "\xA0 ",
        numberOfIssues,
        " Error"
      );
    case "note":
      return /* @__PURE__ */ React.createElement(
        Box,
        {
          display: "flex",
          alignItems: "center",
          fontWeight: "fontWeightLight",
          mr: 2,
          style: { color: "#1976d2" }
        },
        /* @__PURE__ */ React.createElement(NoteRoundedIcon, { fontSize: "small" }),
        "\xA0 ",
        numberOfIssues,
        " Note"
      );
    default:
      return "Unknown";
  }
};

const useStyles$2 = makeStyles((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3)
  }
}));
const IssuesCounter = ({
  issues,
  issueStatus = null
}) => {
  const countIssues = (type, severityLevel) => issues.reduce(
    (acc, cur) => (cur.state === type || issueStatus === null) && cur.rule.severity === severityLevel ? ++acc : acc,
    0
  );
  const countWarningIssues = countIssues(issueStatus, "warning");
  const countErrorIssues = countIssues(issueStatus, "error");
  const countNoteIssues = countIssues(issueStatus, "note");
  return /* @__PURE__ */ React.createElement(Box, { display: "flex", flexDirection: "column" }, getSeverityBadge("warning", countWarningIssues), getSeverityBadge("error", countErrorIssues), getSeverityBadge("note", countNoteIssues));
};
const SecurityInsightsWidget = () => {
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const classes = useStyles$2();
  const auth = useApi(githubAuthApiRef);
  const { baseUrl, hostname } = useUrl(entity);
  const { value, loading, error } = useAsync(async () => {
    const token = await auth.getAccessToken(["repo"]);
    const octokit = new Octokit({ auth: token });
    const response = await octokit.request(
      "GET /repos/{owner}/{repo}/code-scanning/alerts",
      {
        baseUrl,
        owner,
        repo
      }
    );
    const data = response.data;
    return data;
  }, []);
  return /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "Security Insights",
      className: classes.infoCard,
      deepLink: {
        link: `//${hostname}/${owner}/${repo}/security/code-scanning`,
        title: "Security Insights",
        onClick: (e) => {
          e.preventDefault();
          window.open(`//${hostname}/${owner}/${repo}/security/code-scanning`);
        }
      }
    },
    /* @__PURE__ */ React.createElement(Box, { position: "relative" }, error ? /* @__PURE__ */ React.createElement(Alert, { severity: "error", className: classes.infoCard }, error.message) : null, loading ? /* @__PURE__ */ React.createElement(Box, { my: 3 }, /* @__PURE__ */ React.createElement(Progress, null)) : /* @__PURE__ */ React.createElement(React.Fragment, null, value ? /* @__PURE__ */ React.createElement(
      StructuredMetadataTable,
      {
        metadata: {
          "Open Issues": /* @__PURE__ */ React.createElement(IssuesCounter, { issues: value, issueStatus: "open" })
        }
      }
    ) : null))
  );
};

const GITHUB_REPO_ANNOTATION = "github.com/project-slug";
const useProjectName = (entity) => {
  var _a, _b;
  return (_b = (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GITHUB_REPO_ANNOTATION]) != null ? _b : "";
};

const useStyles$1 = makeStyles$1(
  (theme) => createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120
    },
    dialog: {
      minWidth: "300px"
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    label: {
      color: "inherit"
    }
  })
);
const UpdateSeverityStatusModal = ({
  owner,
  repo,
  severityData,
  id,
  tableData,
  setTableData,
  entity
}) => {
  const classes = useStyles$1();
  const [open, setOpen] = useState(false);
  const [dismissedReason, setDismissedReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const auth = useApi(githubAuthApiRef);
  const { baseUrl } = useUrl(entity);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const [state, dissmiss] = useAsyncFn(async (reason) => {
    if (reason.length === 0)
      return setErrorMsg("Field required");
    const token = await auth.getAccessToken(["repo"]);
    const octokit = new Octokit({ auth: token });
    const response = await octokit.request(
      "PATCH /repos/{owner}/{repo}/code-scanning/alerts/{alert_number}",
      {
        baseUrl,
        owner: "RoadieHQ",
        repo: "backstage",
        alert_number: id,
        state: "dismissed",
        dismissed_reason: reason
      }
    );
    const data = response.data;
    handleClose();
    setTableData(
      tableData.map(
        (element) => element.number === id ? { ...element, state: "dismissed" } : element
      )
    );
    return data;
  }, []);
  const getSeverityState = useCallback(
    (severityState) => {
      switch (severityState) {
        case "open":
          return /* @__PURE__ */ React.createElement(
            Chip,
            {
              label: "Open",
              color: "primary",
              variant: "outlined",
              size: "small",
              classes: {
                label: classes.label
              },
              onClick: handleClickOpen
            }
          );
        case "dismissed":
          return /* @__PURE__ */ React.createElement(
            Chip,
            {
              label: "Dismissed",
              color: "secondary",
              variant: "outlined",
              size: "small",
              classes: {
                label: classes.label
              }
            }
          );
        case "fixed":
          return /* @__PURE__ */ React.createElement(
            Chip,
            {
              label: "Fixed",
              color: "primary",
              variant: "outlined",
              size: "small",
              classes: {
                label: classes.label
              }
            }
          );
        default:
          return "Unknown";
      }
    },
    []
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, getSeverityState(severityData), /* @__PURE__ */ React.createElement(
    Dialog,
    {
      open,
      keepMounted: true,
      onClose: handleClose,
      "aria-labelledby": "alert-dialog-slide-title",
      "aria-describedby": "alert-dialog-slide-description",
      fullWidth: true
    },
    /* @__PURE__ */ React.createElement(DialogTitle, { id: "alert-dialog-slide-title" }, "Dismiss severity #", id),
    /* @__PURE__ */ React.createElement(DialogContent, null, state.error && /* @__PURE__ */ React.createElement(Box$1, { mb: 3 }, /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, state.error.message)), /* @__PURE__ */ React.createElement(
      FormControl,
      {
        error: Boolean(errorMsg),
        className: classes.formControl
      },
      /* @__PURE__ */ React.createElement(InputLabel, { id: "dissmis-type-select-error-label" }, "Dismiss type"),
      /* @__PURE__ */ React.createElement(
        Select,
        {
          labelId: "dissmis-type-select-error-label",
          id: "dissmis-type",
          value: dismissedReason,
          onChange: (e) => {
            setErrorMsg("");
            setDismissedReason(e.target.value);
          },
          autoWidth: true
        },
        /* @__PURE__ */ React.createElement(MenuItem, { value: "false positive" }, "False positive"),
        /* @__PURE__ */ React.createElement(MenuItem, { value: "won't fix" }, "Won't fix"),
        /* @__PURE__ */ React.createElement(MenuItem, { value: "used in tests" }, "Used in tests")
      ),
      /* @__PURE__ */ React.createElement(FormHelperText, null, errorMsg)
    )),
    /* @__PURE__ */ React.createElement(DialogActions, null, /* @__PURE__ */ React.createElement(Button, { onClick: handleClose, variant: "contained", color: "secondary" }, "Close"), /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: () => dissmiss(dismissedReason),
        variant: "contained",
        color: "primary"
      },
      "Confirm"
    ))
  ));
};

const StateFilterComponent = ({
  insightsStatusFilter,
  value,
  setInsightsStatusFilter,
  setFilteredTableData
}) => /* @__PURE__ */ React.createElement(Paper, null, /* @__PURE__ */ React.createElement(Box, { position: "absolute", right: 350, top: 20 }, /* @__PURE__ */ React.createElement(ButtonGroup, { color: "primary", "aria-label": "text primary button group" }, /* @__PURE__ */ React.createElement(
  Button$1,
  {
    color: insightsStatusFilter === "open" ? "primary" : "default",
    onClick: () => {
      setInsightsStatusFilter(
        insightsStatusFilter === "open" ? null : "open"
      );
      if (value) {
        setFilteredTableData(
          value.filter((entry) => entry.state === "open")
        );
      }
    }
  },
  "OPEN"
), /* @__PURE__ */ React.createElement(
  Button$1,
  {
    color: insightsStatusFilter === "fixed" ? "primary" : "default",
    onClick: () => {
      setInsightsStatusFilter(
        insightsStatusFilter === "fixed" ? null : "fixed"
      );
      if (value) {
        setFilteredTableData(
          value.filter((entry) => entry.state === "fixed")
        );
      }
    }
  },
  "FIXED"
), /* @__PURE__ */ React.createElement(
  Button$1,
  {
    color: insightsStatusFilter === "dismissed" ? "primary" : "default",
    onClick: () => {
      setInsightsStatusFilter(
        insightsStatusFilter === "dismissed" ? null : "dismissed"
      );
      if (value) {
        setFilteredTableData(
          value.filter((entry) => entry.state === "dismissed")
        );
      }
    }
  },
  "DISMISSED"
))));

const getElapsedTime = (start) => {
  return moment(start).fromNow();
};
const SecurityInsightsTable = () => {
  const [insightsStatusFilter, setInsightsStatusFilter] = useState(null);
  const [filteredTableData, setFilteredTableData] = useState(
    []
  );
  const [tableData, setTableData] = useState([]);
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const projectName = useProjectName(entity);
  const auth = useApi(githubAuthApiRef);
  const { baseUrl } = useUrl(entity);
  const { value, loading, error } = useAsync(async () => {
    const token = await auth.getAccessToken(["repo"]);
    const octokit = new Octokit({ auth: token });
    const response = await octokit.request(
      "GET /repos/{owner}/{repo}/code-scanning/alerts",
      {
        baseUrl,
        owner,
        repo,
        per_page: 100
      }
    );
    const data = response.data;
    setTableData(data);
    return data;
  }, []);
  const generatedColumns = [
    {
      title: "ID",
      field: "number",
      width: "50px",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement("a", { target: "_blank", rel: "noopener noreferrer", href: row.html_url }, "#", row.number))
    },
    {
      title: "Description",
      field: "rule.description",
      highlight: true,
      render: (row) => {
        var _a;
        return /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, (_a = row == null ? void 0 : row.rule) == null ? void 0 : _a.description);
      }
    },
    {
      title: "Severity",
      field: "rule.severity",
      highlight: true,
      render: (row) => {
        var _a;
        const severityLevel = (_a = row == null ? void 0 : row.rule) == null ? void 0 : _a.severity;
        return /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center", fontWeight: "fontWeightLight" }, severityLevel && getSeverityBadge(severityLevel));
      }
    },
    {
      title: "State",
      field: "state",
      highlight: true,
      render: (row) => row.state && row.number && /* @__PURE__ */ React.createElement(
        UpdateSeverityStatusModal,
        {
          owner,
          repo,
          severityData: row.state,
          id: row.number,
          tableData,
          setTableData,
          entity
        }
      )
    },
    {
      title: "Tool",
      field: "tool.name",
      highlight: true,
      render: (row) => {
        var _a;
        return /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, (_a = row == null ? void 0 : row.tool) == null ? void 0 : _a.name);
      }
    },
    {
      title: "Detected",
      field: "created_at",
      highlight: true,
      render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, getElapsedTime(row.created_at))
    }
  ];
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, /* @__PURE__ */ React.createElement(Grid, { container: true, direction: "row", spacing: 3 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Failed to retrieve security insights information from GitHub. Security Insights plugin may require administrator access to display data correctly")), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Error message: ", error.message))));
  }
  return value ? /* @__PURE__ */ React.createElement(
    Table,
    {
      isLoading: loading,
      options: {
        paging: true,
        padding: "dense"
      },
      data: insightsStatusFilter !== null && filteredTableData || tableData,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(GitHubIcon, null), /* @__PURE__ */ React.createElement(Box, { mr: 1 }), /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, projectName)), /* @__PURE__ */ React.createElement(
        StateFilterComponent,
        {
          insightsStatusFilter,
          value,
          setInsightsStatusFilter,
          setFilteredTableData
        }
      )),
      columns: generatedColumns
    }
  ) : null;
};

const useStyles = makeStyles((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3)
  },
  alertsCount: {
    alignSelf: "center"
  },
  critical: {
    color: theme.palette.type === "dark" ? "#f85149" : "#cf222e"
  },
  high: {
    color: theme.palette.type === "dark" ? "#db6d28" : "#bc4c00"
  },
  medium: {
    color: theme.palette.type === "dark" ? "#d29922" : "#bf8600"
  },
  low: {
    color: theme.palette.type === "dark" ? "#c9d1d9" : "#24292f"
  }
}));
const DependabotAlertInformations = ({
  repository,
  detailsUrl
}) => {
  var _a;
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const minimumConfiguredSeverities = (_a = configApi == null ? void 0 : configApi.getOptionalStringArray(
    "dependabotAlertsConfiguration.severity"
  )) != null ? _a : ["all"];
  const all = repository.vulnerabilityAlerts.nodes.filter(
    (entry) => entry.dismissedAt === null
  ) || null;
  const criticalCount = all.filter(
    (node) => node.securityVulnerability.severity === "CRITICAL"
  ).length;
  const highCount = all.filter(
    (node) => node.securityVulnerability.severity === "HIGH"
  ).length;
  const mediumCount = all.filter(
    (node) => node.securityVulnerability.severity === "MODERATE"
  ).length;
  const lowCount = all.filter(
    (node) => node.securityVulnerability.severity === "LOW"
  ).length;
  return /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "Dependabot Alerts",
      className: classes.infoCard,
      deepLink: {
        link: `https://${detailsUrl.hostname}/${detailsUrl.owner}/${detailsUrl.repo}/security/dependabot`,
        title: "More info",
        onClick: (e) => {
          e.preventDefault();
          window.open(
            `//${detailsUrl.hostname}/${detailsUrl.owner}/${detailsUrl.repo}/security/dependabot`
          );
        }
      }
    },
    /* @__PURE__ */ React.createElement(Box, { "data-testid": "severitiesContainer", display: "flex" }, (minimumConfiguredSeverities.length > 0 && minimumConfiguredSeverities.includes("critical") || minimumConfiguredSeverities.includes("all")) && /* @__PURE__ */ React.createElement(
      Box,
      {
        "data-testid": "severityLevel",
        ml: 2,
        mr: 2,
        display: "flex",
        className: classes.critical,
        justifyContent: "center",
        flexDirection: "column"
      },
      /* @__PURE__ */ React.createElement(Typography, { className: classes.alertsCount, variant: "h1" }, criticalCount),
      /* @__PURE__ */ React.createElement(Typography, null, " Critical severity ")
    ), (minimumConfiguredSeverities.length > 0 && minimumConfiguredSeverities.includes("high") || minimumConfiguredSeverities.includes("all")) && /* @__PURE__ */ React.createElement(
      Box,
      {
        "data-testid": "severityLevel",
        ml: 2,
        mr: 2,
        display: "flex",
        className: classes.high,
        justifyContent: "center",
        flexDirection: "column"
      },
      /* @__PURE__ */ React.createElement(Typography, { className: classes.alertsCount, variant: "h1" }, highCount),
      /* @__PURE__ */ React.createElement(Typography, null, " High severity ")
    ), (minimumConfiguredSeverities.length > 0 && minimumConfiguredSeverities.includes("medium") || minimumConfiguredSeverities.includes("all")) && /* @__PURE__ */ React.createElement(
      Box,
      {
        "data-testid": "severityLevel",
        ml: 2,
        mr: 2,
        display: "flex",
        className: classes.medium,
        justifyContent: "center",
        flexDirection: "column"
      },
      /* @__PURE__ */ React.createElement(Typography, { className: classes.alertsCount, variant: "h1" }, mediumCount),
      /* @__PURE__ */ React.createElement(Typography, null, " Medium severity ")
    ), (minimumConfiguredSeverities.length > 0 && minimumConfiguredSeverities.includes("low") || minimumConfiguredSeverities.includes("all")) && /* @__PURE__ */ React.createElement(
      Box,
      {
        "data-testid": "severityLevel",
        ml: 2,
        mr: 2,
        display: "flex",
        className: classes.low,
        justifyContent: "center",
        flexDirection: "column"
      },
      /* @__PURE__ */ React.createElement(Typography, { className: classes.alertsCount, variant: "h1" }, lowCount),
      /* @__PURE__ */ React.createElement(Typography, null, " Low severity ")
    ))
  );
};
const DependabotAlertsWidget = () => {
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const auth = useApi(githubAuthApiRef);
  const { hostname, baseUrl } = useUrl(entity);
  const query = `
  query GetDependabotAlertsWidget($name: String!, $owner: String!) {
    repository(name: $name, owner: $owner) {
      vulnerabilityAlerts(first: 100, states: OPEN) {
        totalCount
        nodes {
          state
          createdAt
          id
          dismissedAt
          vulnerableManifestPath
          securityVulnerability {
            vulnerableVersionRange
            package {
              name
            }
            firstPatchedVersion {
              identifier
            }
            severity
            advisory {
              description
            }
          }
        }
      }
    }
  }`;
  const { value, loading, error } = useAsync(async () => {
    const token = await auth.getAccessToken(["repo"]);
    const gqlEndpoint = graphql.defaults({
      baseUrl,
      headers: {
        authorization: `token ${token}`
      }
    });
    const { repository } = await gqlEndpoint(query, {
      name: repo,
      owner
    });
    return repository;
  }, []);
  const detailsUrl = { hostname, owner, repo };
  if (loading)
    return /* @__PURE__ */ React.createElement(Progress, null);
  if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, /* @__PURE__ */ React.createElement(Grid, { container: true, direction: "row", spacing: 3 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Failed to retrieve Dependabot information from GitHub. Security Insights plugin may require administrator access to display data correctly")), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Error message: ", error.message))));
  }
  return value && value.vulnerabilityAlerts ? /* @__PURE__ */ React.createElement(DependabotAlertInformations, { repository: value, detailsUrl }) : null;
};

const capitalize = (s) => {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
};
const getDetailsUrl = (packageName, detailsUrl, state, vulnerableManifestPath) => {
  const url = new URL(
    `https://${detailsUrl.hostname}/${detailsUrl.owner}/${detailsUrl.repo}/security/dependabot`
  );
  const queryParts = [
    `is:${state === "OPEN" ? "open" : "closed"}`,
    `manifest:${vulnerableManifestPath}`,
    `package:${packageName}`
  ];
  url.searchParams.set("q", queryParts.join(" "));
  return /* @__PURE__ */ React.createElement(Link, { to: url.toString() }, packageName);
};
const DenseTable = ({ repository, detailsUrl }) => {
  const { entity } = useEntity();
  const projectName = useProjectName(entity);
  const [filteredTableData, setFilteredTableData] = useState(
    repository.vulnerabilityAlerts.nodes
  );
  const [stateFilter, setStateFilter] = useState("ALL");
  const filterAlerts = (newStateFilter) => {
    setStateFilter(newStateFilter);
    const issues = repository.vulnerabilityAlerts.nodes;
    if (newStateFilter === "ALL") {
      setFilteredTableData(issues);
    } else {
      setFilteredTableData(
        issues.filter((node) => node.state === newStateFilter)
      );
    }
  };
  const columns = [
    { title: "Name", field: "name" },
    { title: "Created", field: "createdAt" },
    { title: "State", field: "state" },
    { title: "Severity", field: "severity" },
    { title: "Patched Version", field: "patched_version" }
  ];
  const tableData = filteredTableData.length > 0 ? filteredTableData : [];
  const structuredData = tableData.map((node) => {
    var _a, _b;
    return {
      createdAt: DateTime.fromISO(node.createdAt).toLocaleString(),
      state: capitalize(node.state),
      name: getDetailsUrl(
        node.securityVulnerability.package.name,
        detailsUrl,
        node.state,
        node.vulnerableManifestPath
      ),
      severity: capitalize(node.securityVulnerability.severity.toLowerCase()),
      patched_version: ((_b = (_a = node == null ? void 0 : node.securityVulnerability) == null ? void 0 : _a.firstPatchedVersion) == null ? void 0 : _b.identifier) || ""
    };
  });
  return /* @__PURE__ */ React.createElement(
    Table,
    {
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(GitHubIcon, null), /* @__PURE__ */ React.createElement(Box, { mr: 1 }), /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, projectName)), /* @__PURE__ */ React.createElement(Paper, null, /* @__PURE__ */ React.createElement(Box, { position: "absolute", right: 350, top: 20 }, /* @__PURE__ */ React.createElement(
        ButtonGroup,
        {
          color: "primary",
          "aria-label": "text primary button group"
        },
        /* @__PURE__ */ React.createElement(
          Button$1,
          {
            color: stateFilter === "ALL" ? "primary" : "default",
            onClick: () => filterAlerts("ALL")
          },
          "ALL"
        ),
        /* @__PURE__ */ React.createElement(
          Button$1,
          {
            color: stateFilter === "OPEN" ? "primary" : "default",
            onClick: () => filterAlerts("OPEN")
          },
          "OPEN"
        ),
        /* @__PURE__ */ React.createElement(
          Button$1,
          {
            color: stateFilter === "FIXED" ? "primary" : "default",
            onClick: () => filterAlerts("FIXED")
          },
          "FIXED"
        ),
        /* @__PURE__ */ React.createElement(
          Button$1,
          {
            color: stateFilter === "DISMISSED" ? "primary" : "default",
            onClick: () => filterAlerts("DISMISSED")
          },
          "DISMISSED"
        )
      )))),
      options: { search: true, paging: true, padding: "dense" },
      columns,
      data: structuredData
    }
  );
};
const DependabotAlertsTable = () => {
  const { entity } = useEntity();
  const { hostname, baseUrl } = useUrl(entity);
  const { owner, repo } = useProjectEntity(entity);
  const auth = useApi(githubAuthApiRef);
  const query = `
  query GetDependabotAlerts($name: String!, $owner: String!) {
    repository(name: $name, owner: $owner) {
      vulnerabilityAlerts(first: 100) {
        totalCount
        nodes {
          state
          createdAt
          id
          vulnerableManifestPath
          securityVulnerability {
            vulnerableVersionRange
            package {
              name
            }
            firstPatchedVersion {
              identifier
            }
            severity
            advisory {
              description
            }
          }
        }
      }
    }
  }`;
  const { value, loading, error } = useAsync(async () => {
    const token = await auth.getAccessToken(["repo"]);
    const gqlEndpoint = graphql.defaults({
      baseUrl,
      headers: {
        authorization: `token ${token}`
      }
    });
    const { repository } = await gqlEndpoint(query, {
      name: repo,
      owner
    });
    return repository;
  }, []);
  const detailsUrl = { hostname, owner, repo };
  if (loading)
    return /* @__PURE__ */ React.createElement(Progress, null);
  if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, /* @__PURE__ */ React.createElement(Grid, { container: true, direction: "row", spacing: 3 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Failed to retrieve Dependabot alerts from GitHub. Security Insights plugin may require administrator access to display data correctly")), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, null, "Error message: ", error.message))));
  }
  return value && value.vulnerabilityAlerts ? /* @__PURE__ */ React.createElement(DenseTable, { repository: value, detailsUrl }) : null;
};

const isSecurityInsightsAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity.metadata.annotations) == null ? void 0 : _a[GITHUB_REPO_ANNOTATION]);
};
const SecurityInsightsTab = () => {
  const { entity } = useEntity();
  if (!isSecurityInsightsAvailable(entity)) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_REPO_ANNOTATION });
  }
  return /* @__PURE__ */ React.createElement(Page, { themeId: "tool" }, /* @__PURE__ */ React.createElement(Content, null, /* @__PURE__ */ React.createElement(ContentHeader, { title: "Security Insights" }, /* @__PURE__ */ React.createElement(SupportButton, null, "Plugin to show Security Insights")), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 3, direction: "column" }, /* @__PURE__ */ React.createElement(Grid, { item: true }, /* @__PURE__ */ React.createElement(SecurityInsightsTable, null)))));
};

const Router = () => {
  return /* @__PURE__ */ React.createElement(Routes, null, /* @__PURE__ */ React.createElement(Route, { element: /* @__PURE__ */ React.createElement(SecurityInsightsTab, null) }));
};

export { DependabotAlertsTable as D, EntitySecurityInsightsContent as E, GITHUB_REPO_ANNOTATION as G, Router as R, SecurityInsightsTab as S, SecurityInsightsWidget as a, DependabotAlertsWidget as b, EntityGithubDependabotContent as c, EntitySecurityInsightsCard as d, EntityDependabotAlertsCard as e, SecurityInsightsTable as f, isSecurityInsightsAvailable as i, securityInsightsPlugin as s };
//# sourceMappingURL=index-04fea838.esm.js.map
